import { useEffect, useState } from 'react';
import axios from 'axios';
import { isCookieAllowed, hasConsentChoice } from '../components/CookieBanner';

// Global flag to prevent multiple GA initializations
let gaInitialized = false;

/**
 * Hook to initialize Google Analytics from database config
 * Should be called once at the App level
 * Respects cookie consent preferences
 */
export function useGoogleAnalytics() {
  const [gaId, setGaId] = useState(null);
  const [analyticsAllowed, setAnalyticsAllowed] = useState(false);

  // Check consent and fetch config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get('/api/config');
        if (response.data?.google_analytics_id) {
          setGaId(response.data.google_analytics_id);
        }
      } catch (error) {
        console.log('Could not fetch config for GA');
      }
    };

    // Check if analytics cookies are allowed
    const checkConsent = () => {
      const allowed = isCookieAllowed('analytics');
      setAnalyticsAllowed(allowed);
    };

    fetchConfig();
    checkConsent();

    // Re-check consent periodically (in case user changes preferences)
    const interval = setInterval(checkConsent, 2000);
    return () => clearInterval(interval);
  }, []);

  // Initialize GA when ID is available AND analytics cookies are allowed
  useEffect(() => {
    if (gaId && analyticsAllowed && !gaInitialized) {
      gaInitialized = true;

      // Load gtag.js script
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      script1.id = 'ga-gtag-script';
      document.head.appendChild(script1);

      // Initialize gtag
      const script2 = document.createElement('script');
      script2.id = 'ga-config-script';
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${gaId}');
      `;
      document.head.appendChild(script2);

      console.log('Google Analytics initialized:', gaId);
    }
  }, [gaId, analyticsAllowed]);

  return gaId;
}

/**
 * Track a page view manually (for SPA navigation)
 * Only tracks if analytics cookies are allowed
 */
export function trackPageView(path) {
  if (window.gtag && isCookieAllowed('analytics')) {
    window.gtag('event', 'page_view', {
      page_path: path,
    });
  }
}

/**
 * Track custom events
 * Only tracks if analytics cookies are allowed
 */
export function trackEvent(eventName, parameters = {}) {
  if (window.gtag && isCookieAllowed('analytics')) {
    window.gtag('event', eventName, parameters);
  }
}

export default useGoogleAnalytics;
